/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Provider marks. AWS, Azure, Nutanix, IBM (Power) and IBM Cloud use the Tech9IQ marks (the
 * sprite shared with the Ops Console, ui/IconTile/tech9iq-cloud-marks.svg), so a cloud looks the
 * same in every Tech9IQ module; the on-prem hub (provider id "metal") is a Server icon tile and
 * reads "On-prem". Every other provider keeps its vendor logo (/api/logos/...).
 *
 * The sprite is inlined into the page once (a <use> of a symbol in another file does not work
 * when webpack inlines the small file as a data: URL).
 */

import * as React from "react";
import styled from "styled-components";
import { Server } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";
import spriteUrl from "@src/components/ui/IconTile/tech9iq-cloud-marks.svg";
import configLoader from "@src/utils/Config";

export const TECH9IQ_MARKS: { [provider: string]: string } = {
  aws: "b-aws",
  azure: "b-azure",
  nutanix: "b-nutanix",
  ibm: "b-ibm-cloud",
  ibm_cloud: "b-ibm-cloud",
  ibm_power: "b-ibm",
  powervc: "b-ibm",
};

/** Short names for the Tech9IQ lockups (mark + name). */
const LOCKUP_NAMES: { [provider: string]: string } = {
  aws: "AWS",
  azure: "Azure",
  nutanix: "Nutanix",
  ibm: "IBM Cloud",
  ibm_cloud: "IBM Cloud",
  ibm_power: "IBM Power",
  powervc: "IBM Power",
  metal: "On-prem",
};

export const ON_PREM_PROVIDER = "metal";

export const hasTech9iqMark = (provider?: string | null) =>
  Boolean(provider && (TECH9IQ_MARKS[provider] || provider === ON_PREM_PROVIDER));

/** The display name of a provider ("On-prem" for the bare-metal hub provider). */
export const providerLabel = (provider?: string | null): string => {
  if (!provider) return "";
  if (provider === ON_PREM_PROVIDER) return "On-prem";
  const names: { [p: string]: string } = (configLoader?.config?.providerNames as any) || {};
  return names[provider] || LOCKUP_NAMES[provider] || provider;
};

let spriteRequested = false;
export const ensureMarksSprite = () => {
  if (spriteRequested || typeof document === "undefined" || typeof fetch === "undefined") {
    return;
  }
  if (document.getElementById("t9-cloud-marks")) {
    spriteRequested = true;
    return;
  }
  spriteRequested = true;
  fetch(spriteUrl)
    .then(r => r.text())
    .then(text => {
      if (document.getElementById("t9-cloud-marks")) return;
      const holder = document.createElement("div");
      holder.id = "t9-cloud-marks";
      holder.setAttribute("aria-hidden", "true");
      holder.style.cssText = "position:absolute;width:0;height:0;overflow:hidden";
      holder.innerHTML = text;
      document.body.insertBefore(holder, document.body.firstChild);
    })
    .catch(() => {
      spriteRequested = false;
    });
};

const Svg = styled.svg`
  display: block;
  flex-shrink: 0;
`;

type MarkProps = {
  provider?: string | null;
  size?: number;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
};

/** A square mark (Tech9IQ sprite, or the On-prem Server tile). Null for other providers. */
export const ProviderMark = ({ provider, size = 20, title, className, style }: MarkProps) => {
  React.useEffect(() => {
    ensureMarksSprite();
  }, []);
  if (!provider) return null;
  if (provider === ON_PREM_PROVIDER) {
    return <IconTile icon={Server} size={size} tone="brand" title={title} className={className} />;
  }
  const id = TECH9IQ_MARKS[provider];
  if (!id) return null;
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={style}
      role="img"
      aria-label={title || providerLabel(provider)}
      data-test-id={`providerMark-${provider}`}
    >
      {title ? <title>{title}</title> : null}
      <use href={`#${id}`} />
    </Svg>
  );
};

// ------------------------------------------------------------------ lockups

const Lockup = styled.div<{ vertical?: boolean; gap: number; fontSize: number; white?: boolean; disabled?: boolean }>`
  display: flex;
  flex-direction: ${props => (props.vertical ? "column" : "row")};
  align-items: center;
  justify-content: center;
  gap: ${props => props.gap}px;
  font-size: ${props => props.fontSize}px;
  line-height: 1.2;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${props => (props.white ? "white" : "var(--t9-text-body)")};
  ${props => (props.disabled ? "opacity: 0.45; filter: grayscale(1);" : "")}
`;

const LOCKUP_SIZES: { [h: number]: { mark: number; font: number; gap: number; vertical?: boolean } } = {
  20: { mark: 18, font: 12, gap: 6 },
  32: { mark: 22, font: 13, gap: 6 },
  42: { mark: 28, font: 15, gap: 8 },
  64: { mark: 40, font: 20, gap: 10 },
  128: { mark: 64, font: 20, gap: 12, vertical: true },
};

type LockupProps = {
  provider: string;
  height: number;
  white?: boolean;
  disabled?: boolean;
  /** Only the mark, no name (for tight spots). */
  markOnly?: boolean;
};
/** Mark + short name, sized to the EndpointLogos boxes (32, 42, 64, 128) or 20 for tables. */
export const ProviderLockup = ({ provider, height, white, disabled, markOnly }: LockupProps) => {
  const size = LOCKUP_SIZES[height] || LOCKUP_SIZES[32];
  return (
    <Lockup
      vertical={size.vertical}
      gap={size.gap}
      fontSize={size.font}
      white={white}
      disabled={disabled}
      data-test-id={`providerLockup-${provider}`}
      title={providerLabel(provider)}
    >
      <ProviderMark provider={provider} size={size.mark} />
      {markOnly ? null : <span>{LOCKUP_NAMES[provider] || providerLabel(provider)}</span>}
    </Lockup>
  );
};

// ------------------------------------------------------------ table badge

const VendorLogo = styled.div<{ url: string; width: number; height: number }>`
  width: ${props => props.width}px;
  min-width: ${props => props.width}px;
  height: ${props => props.height}px;
  background: url("${props => props.url}") no-repeat center;
  background-size: ${props => Math.round((props.height / 20) * 80)}px ${props => Math.round((props.height / 20) * 32)}px;
`;

type BadgeProps = {
  provider?: string | null;
  /** Row height class: 20 (tables) or 24. */
  height?: number;
  title?: string;
};
/**
 * The provider in a table cell: the Tech9IQ lockup (mark + name) or the vendor's logo, both
 * 20 px high and 80 px wide at most, so the Source → Target column lines up.
 */
export const ProviderBadge = ({ provider, height = 20, title }: BadgeProps) => {
  if (!provider) return null;
  if (hasTech9iqMark(provider)) {
    return (
      <span title={title || providerLabel(provider)} style={{ display: "inline-flex", minWidth: 0 }}>
        <ProviderLockup provider={provider} height={20} />
      </span>
    );
  }
  const known = Object.keys((configLoader?.config?.providerNames as any) || {}).includes(provider);
  if (!known) {
    return (
      <span title={title || provider} style={{ fontSize: 12, fontWeight: 600, color: "var(--t9-text-muted)" }}>
        {providerLabel(provider)}
      </span>
    );
  }
  return (
    <VendorLogo
      url={`/api/logos/${provider}/32`}
      width={Math.round((height / 20) * 80)}
      height={height}
      title={title || providerLabel(provider)}
      role="img"
      aria-label={providerLabel(provider)}
      data-test-id={`providerBadge-${provider}`}
    />
  );
};
